import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  FormModule,
  RowComponent,
  TableDirective
} from '@coreui/angular-pro';
import { Logger } from 'shared';
import { TranslatePipe } from 'i18n';
import { debounceTime, distinctUntilChanged, Subject, switchMap } from 'rxjs';

interface XshopMember {
  id: number;
  email: string;
}

interface SearchUser {
  id: number;
  email: string;
  xshop: boolean;
}

@Component({
  selector: 'app-xshop-admin-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ContainerComponent,
    RowComponent,
    ColComponent,
    ButtonDirective,
    FormModule,
    TableDirective,
    TranslatePipe
  ],
  templateUrl: './xshop-admin-page.component.html',
  styleUrls: ['./xshop-admin-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class XshopAdminPageComponent {
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  private logger = new Logger('XshopAdminPageComponent');
  private searchSubject = new Subject<string>();

  members = signal<XshopMember[]>([]);
  searchResults = signal<SearchUser[]>([]);
  searchQuery = signal('');
  loadingMembers = signal(false);
  loadingSearch = signal(false);
  addingUserId = signal<number | null>(null);
  removingUserId = signal<number | null>(null);

  constructor() {
    this.loadMembers();

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(q => {
        this.loadingSearch.set(true);
        return this.http.get<{ users: SearchUser[] }>('/xshop/search', { params: { q } });
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: res => {
        this.searchResults.set(res.users);
        this.loadingSearch.set(false);
      },
      error: err => {
        this.logger.error('Search failed', err);
        this.loadingSearch.set(false);
      }
    });
  }

  private loadMembers(): void {
    this.loadingMembers.set(true);
    this.http.get<{ members: XshopMember[] }>('/xshop/members')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.members.set(res.members);
          this.loadingMembers.set(false);
        },
        error: err => {
          this.logger.error('Failed to load XShop members', err);
          this.loadingMembers.set(false);
        }
      });
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    if (value.length >= 2) {
      this.searchSubject.next(value);
    } else {
      this.searchResults.set([]);
    }
  }

  addMember(userId: number): void {
    this.addingUserId.set(userId);
    this.http.post<{ id: number; email: string }>('/xshop/members', { user_id: userId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.addingUserId.set(null);
          this.loadMembers();
          // refresh search results to reflect new xshop state
          const q = this.searchQuery();
          if (q.length >= 2) this.searchSubject.next(q);
        },
        error: err => {
          this.logger.error('Failed to add XShop member', err);
          this.addingUserId.set(null);
        }
      });
  }

  removeMember(userId: number): void {
    this.removingUserId.set(userId);
    this.http.delete(`/xshop/members/${userId}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.removingUserId.set(null);
          this.loadMembers();
          const q = this.searchQuery();
          if (q.length >= 2) this.searchSubject.next(q);
        },
        error: err => {
          this.logger.error('Failed to remove XShop member', err);
          this.removingUserId.set(null);
        }
      });
  }

  isMember(userId: number): boolean {
    return this.members().some(m => m.id === userId);
  }
}
